import { useEffect, useState } from 'react'
import { FileText } from 'lucide-react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Badge } from 'src/shared/components/ui/badge'
import { Button } from 'src/shared/components/ui/button'
import { Checkbox } from 'src/shared/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/shared/components/ui/dialog'
import { ScrollArea } from 'src/shared/components/ui/scroll-area'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { isPdfUrl } from 'src/shared/utils/proofUrls'
import type { OrgReuseProofRow } from '../types'

export const ReuseProofDialog = ({
  open,
  onOpenChange,
  rows,
  disabled,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  rows: OrgReuseProofRow[]
  disabled?: boolean
  onConfirm: (urls: string[]) => Promise<boolean>
}) => {
  const { t } = useLocalization()
  const [selected, setSelected] = useState<string[]>([])
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!open) {
      setSelected([])
      setPreviewUrl(null)
    }
  }, [open])

  const handleConfirm = async () => {
    const toAdd = selected.filter(
      (url) => !rows.find((r) => r.url === url)?.alreadyOnThisOrder
    )
    if (toAdd.length === 0) return
    try {
      setIsSubmitting(true)
      const ok = await onConfirm(toAdd)
      if (ok) onOpenChange(false)
    } finally {
      setIsSubmitting(false)
    }
  }

  const addableSelected = selected.filter(
    (url) => !rows.find((r) => r.url === url)?.alreadyOnThisOrder
  )

  const previewRow = previewUrl ? rows.find((r) => r.url === previewUrl) : null
  const previewIsPdf = previewUrl ? isPdfUrl(previewUrl) : false

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl gap-4">
        <DialogHeader>
          <DialogTitle>
            {t('Reuse proofs', 'Återanvänd korrekturer')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'Choose proofs from your organization. Items already on this order are shown for reference only.',
              'Välj korrektur från organisationen. Poster som redan finns på denna order visas bara som referens.'
            )}
          </DialogDescription>
        </DialogHeader>

        {rows.length === 0 ? (
          <span className="text-sm text-muted-foreground">
            {t(
              'No proofs in this organization yet.',
              'Inga korrektur i organisationen ännu.'
            )}
          </span>
        ) : (
          <Row className="items-stretch gap-4">
            <Column className="w-[280px] shrink-0">
              <ScrollArea className="h-[min(55vh,420px)] pr-3">
                <Column className="gap-2">
                  {rows.map((row, index) => {
                    const pdf = isPdfUrl(row.url)
                    const checked = selected.includes(row.url)
                    const id = `reuse-proof-${index}`
                    const isPreviewing = previewUrl === row.url

                    return (
                      <Row
                        key={row.url}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setPreviewUrl(row.url)
                          }
                        }}
                        onClick={() => setPreviewUrl(row.url)}
                        className={`cursor-pointer items-center gap-3 rounded-lg border p-2 transition-colors ${
                          isPreviewing
                            ? 'border-primary bg-primary/5'
                            : 'border-border/80 bg-muted/10 hover:bg-muted/25'
                        }`}
                      >
                        <div
                          className="shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {row.alreadyOnThisOrder ? (
                            <div className="w-[13px]" aria-hidden />
                          ) : (
                            <Checkbox
                              id={id}
                              checked={checked}
                              label=""
                              disabled={disabled || isSubmitting}
                              setChecked={(v) => {
                                const on = v === true
                                setSelected((prev) =>
                                  on
                                    ? prev.includes(row.url)
                                      ? prev
                                      : [...prev, row.url]
                                    : prev.filter((u) => u !== row.url)
                                )
                              }}
                            />
                          )}
                        </div>

                        <div
                          className="pointer-events-none flex size-10 shrink-0 items-center justify-center overflow-hidden rounded border bg-background"
                          aria-hidden
                        >
                          {pdf ? (
                            <FileText className="size-5 text-muted-foreground" />
                          ) : (
                            <img
                              src={row.url}
                              alt=""
                              className="size-full object-cover"
                            />
                          )}
                        </div>

                        <Column className="min-w-0 flex-1 gap-0.5">
                          <span className="truncate text-sm font-medium">
                            {row.filename}
                          </span>
                          <span className="truncate text-xs text-muted-foreground">
                            {row.orderLabel}
                          </span>
                        </Column>

                        {row.alreadyOnThisOrder ? (
                          <Badge
                            variant="secondary"
                            className="pointer-events-none shrink-0 text-xs"
                          >
                            {t('On this order', 'På denna order')}
                          </Badge>
                        ) : null}
                      </Row>
                    )
                  })}
                </Column>
              </ScrollArea>
            </Column>

            <Column className="min-h-[min(55vh,420px)] min-w-0 flex-1 rounded-lg border border-border/80 bg-muted/10 p-3">
              {previewUrl && previewRow ? (
                <Column className="gap-2">
                  <span className="truncate text-sm font-medium">
                    {previewRow.filename}
                  </span>
                  {previewIsPdf ? (
                    <Column className="gap-2">
                      <a
                        href={previewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline"
                      >
                        {t('Open PDF in new tab', 'Öppna PDF i ny flik')}
                      </a>
                      <iframe
                        src={previewUrl}
                        title={previewRow.filename}
                        className="h-[min(48vh,380px)] w-full rounded-md border bg-background"
                      />
                    </Column>
                  ) : (
                    <Column className="flex flex-1 items-center justify-center overflow-auto">
                      <img
                        src={previewUrl}
                        alt={previewRow.filename}
                        className="max-h-[min(48vh,380px)] w-full object-contain"
                      />
                    </Column>
                  )}
                </Column>
              ) : (
                <Column className="flex h-full min-h-[200px] items-center justify-center px-4">
                  <span className="text-center text-sm text-muted-foreground">
                    {t(
                      'Click a proof in the list to preview it here.',
                      'Klicka på ett korrektur i listan för att förhandsgranska det här.'
                    )}
                  </span>
                </Column>
              )}
            </Column>
          </Row>
        )}

        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            {t('Cancel', 'Avbryt')}
          </Button>
          <Button
            onClick={() => void handleConfirm()}
            disabled={
              disabled ||
              isSubmitting ||
              addableSelected.length === 0 ||
              rows.length === 0
            }
          >
            {isSubmitting
              ? t('Adding…', 'Lägger till…')
              : t('Add selected', 'Lägg till valda')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
